<template>
  <div class="page-ai">
    <div class="page-header">
      <h1>Cadastro de Áreas de Inspeção (AI)</h1>
      <p>
        Cadastro das áreas de inspeção vinculadas aos estabelecimentos,
        servindo como nível intermediário entre o estabelecimento e as
        respectivas unidades de inspeção (UI).
      </p>
    </div>

    <div class="content-grid">
      <!-- FORMULÁRIO -->
      <div class="card form-card">
        <div class="card-header">
          <h2>{{ objEdicao.id ? 'Editar Área de Inspeção' : 'Nova Área de Inspeção' }}</h2>
        </div>

        <form class="form-body" @submit.prevent="salvar">
          <div class="form-row">
            <div class="form-group small">
              <label>Código</label>
              <input
                type="text"
                :value="objEdicao.id ? String(objEdicao.id).padStart(3, '0') : 'NOVO'"
                disabled
              />
            </div>

            <div class="form-group">
              <label>Estabelecimento *</label>
              <select v-model="objEdicao.idEstabelecimento">
                <option :value="null">Selecione...</option>
                <option
                  v-for="est in lsEstabelecimentos"
                  :key="est.id"
                  :value="est.id"
                >
                  {{ est.nome }}
                </option>
              </select>
            </div>

            <div class="form-group small">
              <label>Status</label>
              <select v-model="objEdicao.ativo">
                <option value="S">Ativo</option>
                <option value="N">Inativo</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Nome da Área de Inspeção *</label>
              <input
                type="text"
                v-model="objEdicao.descricao"
                maxlength="100"
                placeholder="Ex.: Produção, Expedição, Recepção, Câmara Fria..."
              />
            </div>

            <div class="form-group">
              <label>Tipo / Classificação</label>
              <select v-model="objEdicao.tipo">
                <option value="">Selecione...</option>
                <option value="PRODUCAO">Produção</option>
                <option value="RECEPCAO">Recepção</option>
                <option value="EXPEDICAO">Expedição</option>
                <option value="ARMAZENAMENTO">Armazenamento</option>
                <option value="HIGIENIZACAO">Higienização</option>
                <option value="PROCESSAMENTO">Processamento</option>
                <option value="OUTRA">Outra</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group small">
              <label>Ordem</label>
              <input
                type="number"
                v-model.number="objEdicao.ordem"
                min="0"
                placeholder="0"
              />
            </div>

            <div class="form-group">
              <label>Responsável / Observador</label>
              <input
                type="text"
                v-model="objEdicao.responsavel"
                maxlength="80"
                placeholder="Nome do responsável pela área"
              />
            </div>

            <div class="form-group small">
              <label>Área crítica?</label>
              <select v-model="objEdicao.areaCritica">
                <option value="N">Não</option>
                <option value="S">Sim</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group form-group-full">
              <label>Finalidade / Descrição operacional</label>
              <textarea
                v-model="objEdicao.finalidade"
                rows="3"
                maxlength="250"
                placeholder="Descreva a finalidade da área de inspeção dentro do estabelecimento..."
              ></textarea>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group form-group-full">
              <label>Observações</label>
              <textarea
                v-model="objEdicao.observacoes"
                rows="4"
                maxlength="300"
                placeholder="Informações complementares..."
              ></textarea>
            </div>
          </div>

          <div class="info-panel" v-if="estabelecimentoSelecionado">
            <div><strong>Estabelecimento:</strong> {{ estabelecimentoSelecionado.nome }}</div>
            <div><strong>Classificação:</strong> {{ estabelecimentoSelecionado.classificacao }}</div>
            <div><strong>Município:</strong> {{ estabelecimentoSelecionado.municipio }}</div>
          </div>

          <div class="form-actions">
            <button type="submit" class="btn btn-primary">
              {{ objEdicao.id ? 'Salvar Alterações' : 'Adicionar à Lista' }}
            </button>
            <button type="button" class="btn btn-secondary" @click="limpar">
              Limpar
            </button>
          </div>
        </form>
      </div>

      <!-- LISTA -->
      <div class="card list-card">
        <div class="card-header">
          <h2>Áreas de Inspeção Cadastradas</h2>
          <span class="badge">{{ lsDadosFiltrados.length }} registro(s)</span>
        </div>

        <div class="list-toolbar">
          <input
            type="text"
            v-model="filtro"
            placeholder="Filtrar por estabelecimento, área, tipo ou responsável..."
          />
        </div>

        <div class="table-wrapper">
          <table class="table-list">
            <thead>
              <tr>
                <th>Cód.</th>
                <th>Estabelecimento</th>
                <th>Área de Inspeção</th>
                <th>Tipo</th>
                <th>Crítica</th>
                <th>Responsável</th>
                <th>Status</th>
                <th>Ação</th>
              </tr>
            </thead>

            <tbody>
              <tr v-if="lsDadosFiltrados.length === 0">
                <td colspan="8" class="no-data">
                  Nenhuma área de inspeção encontrada.
                </td>
              </tr>

              <tr
                v-for="item in lsDadosFiltrados"
                :key="item.id"
                class="row-clickable"
                @click="editar(item)"
              >
                <td>{{ String(item.id).padStart(3, '0') }}</td>
                <td>{{ getNomeEstabelecimento(item.idEstabelecimento) }}</td>
                <td>{{ item.descricao }}</td>
                <td>{{ item.tipo || '-' }}</td>
                <td>{{ item.areaCritica === 'S' ? 'Sim' : 'Não' }}</td>
                <td>{{ item.responsavel || '-' }}</td>
                <td>
                  <span
                    class="status"
                    :class="item.ativo === 'S' ? 'status-active' : 'status-inactive'"
                  >
                    {{ item.ativo === 'S' ? 'Ativo' : 'Inativo' }}
                  </span>
                </td>
                <td>
                  <button
                    type="button"
                    class="btn-link"
                    @click.stop="editar(item)"
                  >
                    Editar
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="info-box">
          <strong>Conceito adotado:</strong> cada Área de Inspeção pertence a um
          estabelecimento e servirá de base para o cadastro posterior das Unidades
          de Inspeção (UI).
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';

function novoRegistro() {
  return {
    id: null,
    idEstabelecimento: null,
    descricao: '',
    tipo: '',
    ordem: 0,
    responsavel: '',
    areaCritica: 'N',
    finalidade: '',
    observacoes: '',
    ativo: 'S',
  };
}

const lsEstabelecimentos = ref([
  {
    id: 1,
    nome: 'Frigorífico Modelo Ltda',
    classificacao: 'Carnes e derivados',
    municipio: 'Araranguá/SC',
  },
  {
    id: 2,
    nome: 'Laticínios Serra Azul',
    classificacao: 'Leite e derivados',
    municipio: 'Maracajá/SC',
  },
  {
    id: 3,
    nome: 'Entreposto de Ovos Boa Vista',
    classificacao: 'Ovos e derivados',
    municipio: 'Sombrio/SC',
  },
]);

const lsDados = ref([
  {
    id: 1,
    idEstabelecimento: 1,
    descricao: 'Recepção de Matéria-Prima',
    tipo: 'RECEPCAO',
    ordem: 1,
    responsavel: 'Carlos Mendes',
    areaCritica: 'S',
    finalidade: 'Área destinada ao recebimento e conferência da matéria-prima.',
    observacoes: 'Necessita controle rigoroso de higienização.',
    ativo: 'S',
  },
  {
    id: 2,
    idEstabelecimento: 1,
    descricao: 'Sala de Processamento',
    tipo: 'PROCESSAMENTO',
    ordem: 2,
    responsavel: 'Mariana Souza',
    areaCritica: 'S',
    finalidade: 'Área principal de manipulação e processamento dos produtos.',
    observacoes: '',
    ativo: 'S',
  },
  {
    id: 3,
    idEstabelecimento: 2,
    descricao: 'Câmara Fria',
    tipo: 'ARMAZENAMENTO',
    ordem: 3,
    responsavel: 'João Pereira',
    areaCritica: 'N',
    finalidade: 'Armazenamento refrigerado dos produtos acabados.',
    observacoes: 'Monitoramento contínuo de temperatura.',
    ativo: 'S',
  },
]);

const objEdicao = reactive(novoRegistro());
const filtro = ref('');

const estabelecimentoSelecionado = computed(() => {
  return lsEstabelecimentos.value.find(
    a => a.id === objEdicao.idEstabelecimento
  ) || null;
});

const lsDadosFiltrados = computed(() => {
  const texto = filtro.value.trim().toLowerCase();

  let dados = [...lsDados.value].sort((a, b) => {
    if (a.idEstabelecimento !== b.idEstabelecimento) {
      return (a.idEstabelecimento || 0) - (b.idEstabelecimento || 0);
    }
    return (a.ordem || 0) - (b.ordem || 0);
  });

  if (!texto) return dados;

  return dados.filter(item => {
    const nomeEst = getNomeEstabelecimento(item.idEstabelecimento).toLowerCase();
    return (
      nomeEst.includes(texto) ||
      (item.descricao || '').toLowerCase().includes(texto) ||
      (item.tipo || '').toLowerCase().includes(texto) ||
      (item.responsavel || '').toLowerCase().includes(texto)
    );
  });
});

function getNomeEstabelecimento(idEstabelecimento) {
  const est = lsEstabelecimentos.value.find(a => a.id === idEstabelecimento);
  return est ? est.nome : '-';
}

function limpar() {
  Object.assign(objEdicao, novoRegistro());
}

function editar(item) {
  Object.assign(objEdicao, JSON.parse(JSON.stringify(item)));
}

function salvar() {
  if (!objEdicao.idEstabelecimento) {
    alert('Selecione o estabelecimento.');
    return;
  }

  if (!objEdicao.descricao.trim()) {
    alert('Informe o nome da área de inspeção.');
    return;
  }

  if (objEdicao.id) {
    const idx = lsDados.value.findIndex(a => a.id === objEdicao.id);
    if (idx >= 0) {
      lsDados.value[idx] = JSON.parse(JSON.stringify(objEdicao));
    }
  } else {
    const novoId =
      lsDados.value.length > 0
        ? Math.max(...lsDados.value.map(a => a.id || 0)) + 1
        : 1;

    lsDados.value.push({
      ...JSON.parse(JSON.stringify(objEdicao)),
      id: novoId,
    });
  }

  limpar();
}
</script>

<style scoped>
.page-ai {
  padding: 20px;
  background: #f5f7fb;
  min-height: 100%;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h1 {
  margin: 0 0 8px;
  font-size: 24px;
  color: #1f2937;
}

.page-header p {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
}

.content-grid {
  display: grid;
  grid-template-columns: 430px 1fr;
  gap: 20px;
  align-items: start;
}

.card {
  background: #ffffff;
  border: 1px solid #dbe3ee;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.04);
}

.card-header {
  padding: 16px 18px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-header h2 {
  margin: 0;
  font-size: 18px;
  color: #1f2937;
}

.badge {
  background: #eef2ff;
  color: #4338ca;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 999px;
}

.form-body {
  padding: 18px;
}

.form-row {
  display: flex;
  gap: 14px;
  margin-bottom: 14px;
}

.form-group {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.form-group.small {
  flex: 0 0 100px;
}

.form-group-full {
  flex: 1 1 100%;
}

.form-group label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  border: 1px solid #cfd8e3;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  color: #111827;
  background: #fff;
  box-sizing: border-box;
  outline: none;
  transition: 0.2s;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.10);
}

.form-group textarea {
  resize: vertical;
  min-height: 90px;
}

.info-panel {
  margin-top: 6px;
  padding: 12px 14px;
  border: 1px solid #dbe3ee;
  border-radius: 8px;
  background: #f8fafc;
  font-size: 13px;
  color: #475569;
  display: grid;
  gap: 4px;
}

.form-actions {
  display: flex;
  gap: 10px;
  margin-top: 18px;
}

.btn {
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.btn-primary {
  background: #2563eb;
  color: #fff;
}

.btn-primary:hover {
  background: #1d4ed8;
}

.btn-secondary {
  background: #e5e7eb;
  color: #111827;
}

.btn-secondary:hover {
  background: #d1d5db;
}

.list-toolbar {
  padding: 14px 18px 0;
}

.list-toolbar input {
  width: 100%;
  border: 1px solid #cfd8e3;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  box-sizing: border-box;
}

.table-wrapper {
  padding: 14px 18px 18px;
  overflow-x: auto;
}

.table-list {
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
}

.table-list thead th {
  text-align: left;
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  background: #f8fafc;
  border-bottom: 1px solid #dbe3ee;
  padding: 12px 10px;
}

.table-list tbody td {
  border-bottom: 1px solid #edf2f7;
  padding: 12px 10px;
  font-size: 14px;
  color: #1f2937;
  vertical-align: middle;
}

.row-clickable {
  cursor: pointer;
}

.row-clickable:hover {
  background: #f9fbff;
}

.no-data {
  text-align: center;
  color: #6b7280;
  padding: 30px 10px !important;
}

.status {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-active {
  background: #dcfce7;
  color: #166534;
}

.status-inactive {
  background: #fee2e2;
  color: #991b1b;
}

.btn-link {
  background: transparent;
  border: none;
  color: #2563eb;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.btn-link:hover {
  text-decoration: underline;
}

.info-box {
  margin: 0 18px 18px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #dbe3ee;
  border-radius: 8px;
  font-size: 13px;
  color: #475569;
}

@media (max-width: 1100px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .form-row {
    flex-direction: column;
  }

  .form-group.small {
    flex: 1;
  }
}
</style>